import { UiSchema } from '@rjsf/utils';
import { get } from 'lodash/fp';
import { useCallback, useMemo, VFC } from 'react';

import {
  VersionDiffChangeType,
  WorkflowVersionStepDiff,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateVisualVersionDiff/types';
import {
  NewValueLabel,
  PrevValueLabel,
  ValueDiffLabel,
} from 'dashboard/apps/InquiryTemplateEditor/ValueDiffLabel';
import { PropertyRow } from 'dashboard/apps/InquiryTemplateEditor/VersionDiffTable';
import { valueTransformer } from 'dashboard/lib/workflows';
import { WorkflowActionFromConfig } from 'dashboard/models/workflow-version';
import { Label } from 'design-system/components/Text';

interface WorkflowActionDiffFieldsProps {
  actionParameters: WorkflowActionFromConfig['params'];
  schema: UiSchema;
  schemaKey: string;
  tokensToDisplayValue: Record<string, string>;
  workflowStepDiff?: WorkflowVersionStepDiff;
}

const WorkflowActionDiffFields: VFC<WorkflowActionDiffFieldsProps> = ({
  actionParameters,
  schemaKey,
  schema,
  tokensToDisplayValue,
  workflowStepDiff,
}) => {
  const { getDisplayValue } = useMemo(
    () => valueTransformer(tokensToDisplayValue),
    [tokensToDisplayValue],
  );

  const getFieldDiffFromWorkflowStepDiff = useCallback(
    (fieldName: string) =>
      get(['changes', 'step', `action.params.${schemaKey}.${fieldName}`], workflowStepDiff) || null,
    [schemaKey, workflowStepDiff],
  );

  return (
    <>
      <Label weight='bold'>{schema['ui:title']!}</Label>
      {actionParameters[schemaKey] != null &&
        Object.entries(actionParameters[schemaKey] as Record<string, string>)?.map(
          ([fieldKey, fieldValue]: [fieldKey: string, fieldValue: string]) => {
            const fieldDiff = getFieldDiffFromWorkflowStepDiff(fieldKey);
            let PropertyRowParentDiv: React.ElementType = 'div';
            switch (fieldDiff?.['change-type']) {
              case VersionDiffChangeType.ADDED:
                PropertyRowParentDiv = NewValueLabel;
                break;
              case VersionDiffChangeType.REMOVED:
                PropertyRowParentDiv = PrevValueLabel;
                break;
            }
            return (
              <PropertyRowParentDiv key={fieldKey}>
                <PropertyRow
                  key={fieldKey}
                  textKey={fieldKey}
                  value={
                    fieldDiff?.['change-type'] === VersionDiffChangeType.MODIFIED ? (
                      <ValueDiffLabel
                        valueDiff={{
                          added: fieldDiff.value ? getDisplayValue(fieldDiff.value) : null,
                          removed: fieldDiff['prev-value']
                            ? getDisplayValue(fieldDiff['prev-value'])
                            : null,
                        }}
                        fallbackValue='None'
                        vertical
                      />
                    ) : (
                      getDisplayValue(fieldValue)
                    )
                  }
                />
              </PropertyRowParentDiv>
            );
          },
        )}
    </>
  );
};

export default WorkflowActionDiffFields;
